import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ExcelUtils } from '../lib/Excel';
import { AppEnvironment } from '../app.config';
import { getLastPath } from '../lib/Urls';

enum AddInSyncMessageType {
    StartSession = 'StartSession',
    Ping = 'Ping',
    ListWorksheets = 'ListWorksheets',
    ListTables = 'ListTables',
    ListNamedRanges = 'ListNamedRanges',
    ListTableColumnInfo = 'ListTableColumnInfo',
    GetRangeValuesAndFormulas = 'GetRangeValuesAndFormulas',
    GetCellRangeFormula = 'GetCellRangeFormula',
    ListButtons = 'ListButtons',
    VbaFileUpload = 'VbaFileUpload',
    GetCurrentSelection = 'GetCurrentSelection',
    SetRangeValue = 'SetRangeValue',
    GetWorkbookInfo = 'GetWorkbookInfo',
    InteractDocument = 'InteractDocument',
}

interface AddInSyncMessage {
    id: string;
    inReplyTo?: string,
    type: AddInSyncMessageType;
    data: any;
}

const WsReconnectIntervalSec = 11;
const WsReconnectMaxAttempts = 100;
const WsReconnectBackOutFactor = 1.5;

export type McpMessageDirection = 'in' | 'out';
export interface McpMessageEvent {
    direction: McpMessageDirection;
    message: AddInSyncMessage;
}

// tslint:disable-next-line:no-eval
const AsyncFunction = window.eval('Object.getPrototypeOf(async function(){}).constructor;');

@Injectable({
    providedIn: 'root'
})
export class McpAdapterService implements OnDestroy {

    public sessionId!: string;
    private hostId!: string;
    private fileUrl!: string;

    private ws!: WebSocket;

    private reconnectTimer: any; // ReturnType<setTimeout> | null = null;
    private reconnectAttempts = 0;
    private reconnectBackoutFactor = 1.0;

    private readonly onMessage = new EventEmitter<McpMessageEvent>();
    public readonly onMessageSubject = this.onMessage.asObservable();

    constructor() {
        this.sessionId = this.createSessionId();
        this.fileUrl = Office.context.document.url;
        this.hostId = Office.context.host.toString();
    }

    private createRandomNumberString(len: number): string {
        const chars = '0123456789';
        let result = '';
        for (let i = len; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
        if (result[0] === '0') return this.createRandomNumberString(len);
        else return result;
    }

    private createSessionId(): string {
        this.sessionId = Office.context.document.settings.get('mcp-session-id');
        if (!this.sessionId) {
            this.sessionId = this.createRandomNumberString(6);
            Office.context.document.settings.set('mcp-session-id', this.sessionId);
            Office.context.document.settings.saveAsync();
        }
        return this.sessionId;
    }

    connect(): WebSocket | null {
        // do not reconnect an open or connecting connection
        if (this.ws != null && [WebSocket.OPEN, WebSocket.CONNECTING].indexOf(this.ws.readyState as (0 | 1)) >= 0) {
            return this.ws;
        }

        const baseUrl = AppEnvironment.mcpServerBaseUrl;
        const params = new URLSearchParams();
        params.set('sessionId', this.sessionId);
        params.set('fileUrl', this.fileUrl);
        params.set('hostId', this.hostId);

        // connect
        try {
            this.ws = new WebSocket(`${baseUrl}/ws/addin/mcp?${params.toString()}`);
            this.ws.onmessage = this.onWsMessage.bind(this);
            this.ws.onopen = this.onWsConnected.bind(this);
            this.ws.onclose = this.onWsClose.bind(this);
            this.ws.onerror = this.onWsError.bind(this);
            return this.ws;
        } catch (e) {
            return null;
        }
    }

    private async onWsMessage(event: MessageEvent) {
        const message: AddInSyncMessage = JSON.parse(event.data);

        try {
            this.onMessage.emit({ direction: 'in', message });
        } catch (e) {
            console.error('onMessage', e);
        }

        switch (message.type) {
            case AddInSyncMessageType.StartSession:
                break;

            case AddInSyncMessageType.Ping:
                this.sendReplyMessage(message, { text: `pong ${message.data.text}` });
                break;

            case AddInSyncMessageType.VbaFileUpload:
                await this.replyWith(message, this.vbaFileUpload.bind(this));
                break;

            case AddInSyncMessageType.ListWorksheets:
                await this.replyWith(message, this.listWorksheets.bind(this));
                break;

            case AddInSyncMessageType.ListTables:
                await this.replyWith(message, this.listTables.bind(this));
                break;

            case AddInSyncMessageType.ListNamedRanges:
                await this.replyWith(message, this.listNamedRanges.bind(this));
                break;

            case AddInSyncMessageType.ListTableColumnInfo:
                await this.replyWith(message, this.listTableColumnInfo.bind(this));
                break;

            case AddInSyncMessageType.GetRangeValuesAndFormulas:
                await this.replyWith(message, this.getRangeValuesAndFormulas.bind(this));
                break;

            case AddInSyncMessageType.GetCellRangeFormula:
                await this.replyWith(message, this.getCellRangeFormula.bind(this));
                break;

            case AddInSyncMessageType.ListButtons:
                await this.replyWith(message, this.listButtons.bind(this));
                break;

            case AddInSyncMessageType.GetWorkbookInfo:
                await this.replyWith(message, this.getWorkbookInfo.bind(this));
                break;

            case AddInSyncMessageType.GetCurrentSelection:
                await this.replyWith(message, this.getCurrentSelection.bind(this));
                break;

            case AddInSyncMessageType.SetRangeValue:
                await this.replyWith(message, this.setRangeValue.bind(this));
                break;

            case AddInSyncMessageType.InteractDocument:
                await this.replyWith(message, this.interactDocument.bind(this));
                break;

            default:
                console.log('SyncMessage: unknown type', event.data);
        }

    }

    sendMessage(type: AddInSyncMessageType, data: any) {
        const id = self.crypto.randomUUID();
        const message: AddInSyncMessage = { id, type, data };
        this.ws.send(JSON.stringify(message));
        this.onMessage.emit({ direction: 'out', message });
    }

    sendReplyMessage(message: AddInSyncMessage, data: any) {
        const id = self.crypto.randomUUID();
        const reply: AddInSyncMessage = { id, inReplyTo: message.id, type: message.type, data };
        this.ws.send(JSON.stringify(reply));
        this.onMessage.emit({ direction: 'out', message: reply });
    }

    /** WS connected, try to send authentication data, if we have any. */
    private async onWsConnected(event: Event) {
        console.log('onWsConnected', event);
        this.resetBackoutStrategy();
        this.sendMessage(AddInSyncMessageType.StartSession, { startTime: new Date().toISOString() });
    }

    /** The close() event on the websocket is nothing we expect, so we try to reconnect. */
    private onWsClose(event: CloseEvent) {
        console.log('onWsClose', event);
        this.reconnect();
    }

    /** Any errors need to be answered with an attempt to reconnect.. */
    private onWsError(event: Event) {
        console.log('onWsError', event);
        this.reconnect();
    }

    private resetBackoutStrategy() {
        this.reconnectAttempts = 0;
        this.reconnectBackoutFactor = 1.0;
    }

    private reconnect() {
        // do not reconnect if we are already reconnecting
        if (this.reconnectTimer != null) return;

        // do not reconnect if we have already too many attempts
        if (this.reconnectAttempts++ > WsReconnectMaxAttempts) {
            console.log(`Too many reconnect attempts (${this.reconnectAttempts}, giving up`);
            return;
        }

        // compute next delay, and increase the backout factor for the next reconnect attempt
        const delayInSecs = WsReconnectIntervalSec * this.reconnectBackoutFactor;
        this.reconnectBackoutFactor = Math.min(10, this.reconnectBackoutFactor * WsReconnectBackOutFactor);

        console.warn(`Reconnecting in ${delayInSecs.toFixed(0)} seconds...`);

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delayInSecs * 1000);
    }

    private async vbaFileUpload() {
        const baseUrl = AppEnvironment.mcpServerBaseUrl;
        const slices = await this.getDocumentInSlices(true) as BlobPart[];
        const uploadUrl = `${baseUrl}/xlsm/vba/${this.sessionId}`;

        const maxAttempts = 3;
        const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

        // create a Blob from the Uint8Array slice
        const file = new Blob(slices, { type: 'application/vnd.ms-excel.sheet.macroEnabled.12' });
        const formData = new FormData();
        formData.append('file', file, Office.context.document.url);

        const resp = await fetch(uploadUrl, {
            method: 'POST',
            body: formData
        });

        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            throw new Error(`Upload failed (status ${resp.status}): ${text}`);
        }
    }

    ngOnDestroy(): void {
    }

    // The following example gets the document in Office Open XML ("compressed") format in 65536 bytes (64 KB) slices.
    // Note: The implementation of app.showNotification in this example is from the Visual Studio template for Office Add-ins.
    // @param compress - whether to compress the document before saving (configured column compression)
    // @param selectA1 - whether to select A1 after saving (to prevent view shifts on next open)
    async getDocumentInSlices(selectA1: boolean = false): Promise<Uint8Array[]> {

        await ExcelUtils.run(async (context) => {
            context.workbook.save(Excel.SaveBehavior.save)
        });

        return new Promise((resolve, reject) => {
            Office.context.document.getFileAsync(Office.FileType.Compressed, { /* sliceSize: 1024*1024*/ },
                async (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        // If the getFileAsync call succeeded, then
                        // result.value will return a valid File Object.
                        const myFile = result.value;
                        const sliceCount = myFile.sliceCount;

                        console.log('File size:' + myFile.size + ' #Slices: ' + sliceCount);

                        // Get the file slices.
                        try {
                            const slices: Uint8Array[] = [];
                            for (let i = 0; i < sliceCount; i++) {
                                const slice = await this.getSliceAsync(myFile, i);
                                slices.push(slice);
                            }
                            resolve(slices);
                        } catch (e) {
                            reject(e);
                        } finally {
                            myFile.closeAsync(() => console.log('File closed'));
                        }
                    } else {
                        console.error('Error:', result.error.message);
                        reject(result.error);
                    }
                });
        });
    }

    private async getSliceAsync(file: Office.File, nextSlice: number): Promise<Uint8Array> {
        return new Promise((resolve, reject) => {
            file.getSliceAsync(nextSlice, async (sliceResult) => {
                if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                    console.log(`Slice ${nextSlice} of ${file.sliceCount} loaded with size ${sliceResult.value.data.length}`);

                    // data is returned in a normal array, not Uint8Array - we have to copy it
                    const retval = new Uint8Array(sliceResult.value.data.length);
                    retval.set(sliceResult.value.data);
                    resolve(retval);
                } else {
                    console.error('getSliceAsync Error:', sliceResult.error.message);
                    reject(sliceResult.error);
                }
            });
        });
    }

    private async listWorksheets(message: AddInSyncMessage): Promise<string> {
        return await ExcelUtils.run(async (ctx) => {
            ctx.workbook.worksheets.load('items/name,items/visibility,items/protection/isPasswordProtected,items/usedRange/address');
            await ctx.sync();

            const usedRanges = ctx.workbook.worksheets.items.map(ws => ws.getUsedRange().load('address'));
            await ctx.sync();

            const sheets = ctx.workbook.worksheets.items.map((ws, index) => {
                const usedRange = usedRanges[index];
                return ` * name: '${ws.name}', used range: ${usedRange.address}, visibility: ${ws.visibility}, protection: ${ws.protection.isPasswordProtected}`;
            });

            return sheets.join('\n')
        });
    }

    private async listTables(message: AddInSyncMessage): Promise<string> {
        return await ExcelUtils.run(async (ctx) => {
            ctx.workbook.tables.load('items/name,items/worksheet/name,items/range/address,items/rows/count,items/columns/count');
            await ctx.sync();

            const ranges = ctx.workbook.tables.items.map(table => table.getRange().load('address'));
            await ctx.sync();

            const tables = ctx.workbook.tables.items.map((table, idx) => {
                const sheet = table.worksheet;
                const range = ranges[idx];
                return `" * table '${table.name}' on sheet '${sheet.name}' at ${range.address}, columns: ${table.columns.count}, rows: ${table.rows.count}`;
            });

            return tables.join('\n');
        })
    }

    private async listNamedRanges(message: AddInSyncMessage): Promise<string> {
        return await ExcelUtils.run(async (ctx) => {
            ctx.workbook.names.load('items/name,items/formula');
            await ctx.sync();

            const names = ctx.workbook.names.items.map(name => {
                const formula = name.formula;
                return ` * name: '${name.name}', refersTo: '${formula}'`;
            });

            return names.join('\n');
        })
    }

    private async listTableColumnInfo(message: AddInSyncMessage): Promise<string> {
        const tableName = message.data.tableName;

        return await ExcelUtils.run(async (ctx) => {
            const table = ctx.workbook.tables.getItem(tableName);
            table.columns.load('items/name,items/index,items/width,items/filterButton/state');
            await ctx.sync();

            const firstCells = table.columns.items.map(column => {
                return column.getDataBodyRange().getCell(0, 0).load('values,formulas,address');
            });
            await ctx.sync();

            const lines = table.columns.items.map((column, idx) => {
                const firstCell = firstCells[idx];
                const firstCellValue = firstCell.values[0][0];
                const firstCellValueDataType = this.guessDataType(firstCellValue);
                const firstCellFormula = this.formulaText(firstCell.formulas[0][0]);
                return ` * column: '${column.name}', type: '${firstCellValueDataType}', formula: ${firstCellFormula ? `'${firstCellFormula}'` : '<none>'}`
            });
            return lines.join('\n');
        });
    }

    private async getRangeValuesAndFormulas(message: AddInSyncMessage): Promise<string> {
        const rangeNameOrAddress = message.data.rangeNameOrAddress;
        const returnFormulas = !!message.data.returnFormulas;

        return await ExcelUtils.run(async (ctx) => {
            // Try to resolve a named range first
            const range = ExcelUtils.getRange(ctx.workbook, rangeNameOrAddress);
            range.load('values,formulas');
            await ctx.sync();

            const values = range.values as any[][];
            const formulas = range.formulas as any[][];

            const cellFormulaValue = (value: any) => this.formulaText(value) ?? ''
            const rangeValuesAsText = values.map(row => '[' + row.map(this.valueText).join(',') + ']').join(',\n');
            const rangeFormulasAsText = formulas.map(row => '[' + row.map(cellFormulaValue).join(',') + ']').join(',\n');

            if (returnFormulas) {
                return `values:\n${rangeValuesAsText},\n\nformulas:\n${rangeFormulasAsText}`;
            } else {
                return `values:\n${rangeValuesAsText}`;
            }
        });
    }

    private valueText(value: any): string {
        if (value == null) return '""';
        if (typeof value === 'string') return `"${value}"`;
        if (typeof value === 'number') {
            // represent integers without decimal when possible
            if (Number.isInteger(value)) return value.toString();
            else return value.toString();
        }
        if (value instanceof Date) return new Date(value).toISOString();
        return String(value);
    }

    private formulaText(value: any): string | undefined {
        const isFormula = (typeof (value) === 'string') && (value.length > 0) && (value[0] === '=');
        return isFormula ? value : undefined;
    }

    private async getCellRangeFormula(message: AddInSyncMessage): Promise<string> {
        const rangeNameOrAddress = message.data.rangeNameOrAddress;
        const recursive = !!message.data.recursive;
        const direction = (message.data.direction || 'PRECEDENTS').toString().toUpperCase();

        return await ExcelUtils.run(async (ctx) => {
            const range = ExcelUtils.getRange(ctx.workbook, rangeNameOrAddress);

            if (!recursive) {
                range.load('formulas,values,address');
                await ctx.sync();

                return `* address: ${range.address}, ${this.cellContentDescription(range)}`;
            } else {
                const seen = new Set<string>();

                const recurse = async (r: Excel.Range, level: number): Promise<string[]> => {
                    const addr = r.address;
                    if (seen.has(addr)) return [];
                    seen.add(addr);

                    const line = `${'  '.repeat(level)}* address: ${addr}, ${this.cellContentDescription(r)}`;

                    let elements = (direction === 'DEPENDENTS') ? r.getDirectDependents() : r.getDirectPrecedents();
                    elements.load('ranges/items,ranges/items/rowCount,ranges/items/columnCount,ranges/items/address,ranges/items/formulas,ranges/items/values');
                    try {
                        await ctx.sync();
                    } catch (error) {
                        elements = null;
                    }

                    if (!elements || elements.ranges.items.length === 0) return [line];

                    // elements may be a collection with .items or a single range
                    const results: string[] = [];
                    const all = elements.ranges.items.flatMap(it => this.expandRangeToCells(it));
                    await ctx.sync();

                    for (const it of all) { // elements.ranges.items) {
                        const childLines = await recurse(it, level + 1);
                        results.push(...childLines);
                    }

                    return [line, ...results];
                };

                range.load('formulas,values,address');
                await ctx.sync();
                const lines = await recurse(range, 0);
                return lines.join('\n');
            }
        });
    }

    private async getCurrentSelection(message: AddInSyncMessage): Promise<string> {
        return await ExcelUtils.run(async (ctx) => {
            const range = ctx.workbook.getSelectedRange().getCell(0, 0);
            range.load('address,values,formulas');
            await ctx.sync();

            return `* address: ${range.address}, ${this.cellContentDescription(range)}`;
        });
    }

    private async setRangeValue(message: AddInSyncMessage): Promise<string> {
        const rangeNameOrAddress = message.data.rangeNameOrAddress;
        const value = message.data.value;

        return await ExcelUtils.run(async (ctx) => {
            const range = ExcelUtils.getRange(ctx.workbook, rangeNameOrAddress);
            range.values = [[value]];
            await ctx.sync();

            return ``;
        });
    }

    private async listButtons(message: AddInSyncMessage): Promise<string> {
        return await ExcelUtils.run(async (ctx) => {
            ctx.workbook.worksheets.load('items/name,items/shapes/items/name,items/shapes/items/type');
            await ctx.sync();

            const buttons = ctx.workbook.worksheets.items.flatMap(ws => {
                return ws.shapes.items.map(shape => {
                    return ` * name: '${shape.name}', type: '${shape.type}'`;
                });
            });

            return buttons.join('\n')
        });
    }

    private cellContentDescription(r: Excel.Range): string {
        const valueOrFormula = r.formulas[0][0];
        const formula = this.formulaText(valueOrFormula);
        return formula ? `formula: ${formula}` : `value: ${this.valueText(valueOrFormula)}`;
    }

    private expandRangeToCells(range: Excel.Range): Excel.Range[] {
        const cells: Excel.Range[] = [];
        const rowCount = range.rowCount;
        const colCount = range.columnCount;

        for (let r = 0; r < rowCount; r++) {
            for (let c = 0; c < colCount; c++) {
                const cell = range.getCell(r, c).load('values,formulas,address');
                cells.push(cell);
            }
        }

        return cells;
    }

    private async replyWith(message: AddInSyncMessage, fn: (AddInSyncMessage) => Promise<string>) {
        try {
            const text = await fn(message);
            this.sendReplyMessage(message, { text });
        } catch (e) {
            const error = e instanceof Error ? e.message : String(e);
            this.sendReplyMessage(message, { isError: true, text: error });
        }
    }

    private guessDataType(value: any): string {
        if (value == null) return 'unknown';
        else {
            const type = typeof value;
            if (type === 'string') return 'String';
            if (type === 'boolean') return 'Boolean';
            if (type === 'number') return 'Number';
            if (value instanceof Date) return 'Date';
        }
    }

    public async getWorkbookInfo(): Promise<string> {
        return await ExcelUtils.run(async (ctx) => {
            const workbook = ctx.workbook;

            // attempt to read workbook properties (may not be available in all API versions)
            let author: string | undefined = undefined;
            let created: string | undefined = undefined;
            let title: string | undefined = undefined;

            workbook.properties.load('author,creationDate,title');
            await ctx.sync();

            if (workbook.properties) {
                author = workbook.properties.author;
                created = workbook.properties.creationDate?.toISOString();
                title = workbook.properties.title;
            }

            // file url/name and size via Office Common API where possible
            const fileUrlTxt = Office.context.document.url ?? 'file:///unsaved';
            const fileName = getLastPath(new URL(fileUrlTxt));

            // Compose the human-readable summary
            const lines: string[] = [];
            lines.push(`file name: ${fileName}`);
            lines.push(`file url: ${new URL(fileUrlTxt) || '<unknown>'}`);
            lines.push(`author: ${author ?? '<unknown>'}`);
            lines.push(`created: ${created ?? '<unknown>'}`);
            lines.push(`title: ${title ?? '<unknown>'}`);

            return lines.join('\n');
        });
    }

    public async interactDocument(message: AddInSyncMessage): Promise<string> {
        const program = message.data.program.trim();
        // tslint:disable-next-line:no-eval
        // return await eval(program);
        try {
            // const codeWithSourceUrl = `return (async () => { return ${program} })() //# sourceURL=dynamic-code.js`
            const codeWithSourceUrl = `return ${program} //# sourceURL=dynamic-code.js`
            // const codeAsFunction = new AsyncFunction('Excel', codeWithSourceUrl);
            // const result = await codeAsFunction(Excel);
            const result = await window.eval(`(async () => { return ${program} })()`)
            const serializableResult = McpAdapterService.jsonSerializeValueObject(result);
            return serializableResult;
        } catch (err: any) {
            console.error('message:', err.message);
            console.error('stack:', err.stack);
            const errorMessage = err?.stack?.toString() ?? err?.message;
            return `@error: ${errorMessage}`
        }
    }

    static jsonSerializeValueObject(value: any): string | undefined | null {
        if (value === undefined || value === null) return value;
        else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value.toString();
        else return JSON.stringify(value, McpAdapterService.spreadJsObjectReplacer);
    }

    private static spreadJsObjectReplacer(key: string, value: any): any {
        return value;
    }
}

